import { NoSsr } from "@mui/base";
import type { FunctionComponent } from "react";
import { Fragment, memo, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { AppFooter } from "~/components/app-footer";
import { ArticleDivider } from "~/components/article/article-divider";
import { ArticleFooter } from "~/components/article/article-footer";
import { ArticlePaywallChecker } from "~/components/article/article-paywall-checker";
import { ArticlePaywallCheckerProvider } from "~/components/article/article-paywall-checker/contexts";
import { ArticleRender } from "~/components/article/article-render";
import { checkIsPostiesArticle } from "~/components/article/article-render/posties/helpers";
import { checkIsStyleArticle } from "~/components/article/article-render/style-article/helper";
import { ArticleSpeechContextProvider } from "~/components/article/article-speech/contexts";
import { ClientSideSuspense } from "~/components/common/client-side-suspense";
import { ContentReadMoreContextProvider } from "~/components/content/content-read-more";
import { InlineOutstreamAdSlotContextProvider } from "~/components/content/content-schema-render/inline-ad-slot/outstream/div-id";
import { Typeform } from "~/components/typeform";
import { useInfiniteScrollTriggerReference } from "~/lib/hooks";
import type { articleListArticle$key } from "~/queries/__generated__/articleListArticle.graphql";

import { ArticleListContextProvider } from "./contexts";
import {
  useArticlePagination,
  useArticlePaginationChecker,
  useArticleRecommendation,
  useHandlePaginationNavigationHistory,
  useTrackPaginationCurrentArticle,
} from "./hooks";
import { useIsShowTypeform } from "./hooks/show-typeform";
import { Item, StyledLoading } from "./styles";
import { TopBanner } from "./top-banner";

type Props = {
  isBot: boolean;
  reference: articleListArticle$key;
};

export const ArticleList: FunctionComponent<Props> = ({ isBot = false, reference: reference_ }) => {
  const article = useFragment(
    graphql`
      fragment articleListArticle on Article
      @argumentDefinitions(customContents: { type: "[CustomContentInput]", defaultValue: [] }) {
        ...topBannerContent
        ...articleDividerArticle
        ...articleFooterArticle
        ...articlePaginationCheckerArticle
        ...articlePaginationNavigationHistoryArticle @arguments(customContents: $customContents)
        ...articleRecommendationArticle
        ...articlePaywallCheckerContent
        ...articleRenderArticle @arguments(customContents: $customContents)
        ...helpersCheckIsPostiesArticle
        ...helpersCheckIsStyleArticle
        ...contextsContentReadMoreContextProviderContent
        ...contextsArticleListContextProviderArticle
        ...showTypeformArticle
      }
    `,
    reference_,
  );

  const { articleRecommendation } = useArticleRecommendation(article);
  const { data, hasNext, isLoading, loadNext } = useArticlePagination(
    articleRecommendation?.results.infiniteScrollArticleIds ?? [],
    articleRecommendation.results.infiniteScrollArticleMap,
  );
  const { infiniteScrollTriggerReference } = useInfiniteScrollTriggerReference({
    hasNextPage: hasNext,
    isLoading,
    onLoadMore: loadNext,
  });

  type Item = typeof article;
  const items = useMemo(() => [article, ...data] as [Item, ...Item[]], [article, data]);

  const { bindItemReference, currentArticle, itemElementReferences } =
    useTrackPaginationCurrentArticle(items);
  const { currentArticleEntityId } = useHandlePaginationNavigationHistory(
    currentArticle,
    articleRecommendation,
  );

  const { shouldStopPagination } = useArticlePaginationChecker(items);

  const shouldContinuePagination = !isBot && !shouldStopPagination && hasNext;

  const getFooterVariant = (item: Item) => {
    switch (true) {
      case checkIsPostiesArticle(item):
        return "posties";
      case checkIsStyleArticle(item):
        return "magazines-style";
      default:
        return;
    }
  };

  const { isShowTypeform } = useIsShowTypeform(currentArticle, itemElementReferences, items);

  return (
    <InlineOutstreamAdSlotContextProvider>
      <ArticleListContextProvider reference={article}>
        <ArticlePaywallCheckerProvider>
          <NoSsr>{isShowTypeform && <Typeform entityId={currentArticleEntityId} />}</NoSsr>
          {items.map((item, index) => (
            <Fragment key={index}>
              {index > 0 && (
                <>
                  <ArticleDivider reference={article} />
                  <TopBanner reference={item} />
                </>
              )}
              <Item ref={bindItemReference(index)}>
                <ContentReadMoreContextProvider reference={item}>
                  <ArticleSpeechContextProvider>
                    <MemorizedArticleRender reference={item} />
                  </ArticleSpeechContextProvider>
                </ContentReadMoreContextProvider>
              </Item>
              <ClientSideSuspense>
                <ArticlePaywallChecker reference={item} />
                {shouldContinuePagination && index === items.length - 1 && (
                  <>
                    <ArticleDivider reference={article} />
                    <StyledLoading ref={infiniteScrollTriggerReference} />
                  </>
                )}
                {!shouldContinuePagination && index === items.length - 1 && (
                  <>
                    {!isBot && (
                      <ArticleFooter
                        articleRecommendation={{
                          articleIds: articleRecommendation.results.moreOnThisTopicWidgetIds,
                          recommendationId: articleRecommendation.recommendationId,
                        }}
                        divider={<ArticleDivider reference={article} />}
                        reference={article}
                      />
                    )}
                    <AppFooter variant={getFooterVariant(item)} />
                  </>
                )}
              </ClientSideSuspense>
            </Fragment>
          ))}
        </ArticlePaywallCheckerProvider>
      </ArticleListContextProvider>
    </InlineOutstreamAdSlotContextProvider>
  );
};

const MemorizedArticleRender = memo(ArticleRender);

ArticleList.displayName = "ArticleList";
